import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ROLE_LABEL } from "@/lib/constants";
import { PlusCircle, Save, Trash2, X, ShieldCheck, ShieldOff } from "lucide-react";
import { Modal } from "@/pages/Customers";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  async function refresh() {
    setLoading(true);
    try {
      const [u, b, r] = await Promise.all([
        api.get("/users"),
        api.get("/branches"),
        api.get("/admin/roles").catch(() => ({ data: [] })),
      ]);
      setUsers(u.data);
      setBranches(b.data);
      setRoles((r.data || []).filter((x) => x.active));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { refresh(); }, []);

  async function save() {
    try {
      const commission = modal.role === "technician" && modal.commission_rate !== "" && modal.commission_rate != null
        ? Number(modal.commission_rate)
        : (modal.role === "technician" ? null : undefined);
      if (modal.id) {
        const payload = { name: modal.name, role: modal.role, branch_id: modal.branch_id || null, active: modal.active };
        if (modal.password) payload.password = modal.password;
        if (commission !== undefined) payload.commission_rate = commission;
        await api.patch(`/users/${modal.id}`, payload);
        toast.success("Usuario actualizado");
      } else {
        const payload = {
          email: modal.email,
          password: modal.password,
          name: modal.name,
          role: modal.role,
          branch_id: modal.branch_id || null,
        };
        if (commission !== undefined) payload.commission_rate = commission;
        await api.post("/users", payload);
        toast.success("Usuario creado");
      }
      setModal(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function toggleActive(u) {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function del(u) {
    if (!confirm(`¿Eliminar a ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Usuario eliminado");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <div className="max-w-5xl mx-auto space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight">Usuarios</h1>
          <p className="text-slate-500 text-sm mt-1">Gestión de cuentas y permisos.</p>
        </div>
        <button onClick={() => setModal({ email: "", password: "", name: "", role: "technician", branch_id: "", active: true })} className="btn-primary" data-testid="users-new-button">
          <PlusCircle size={18} /> Nuevo usuario
        </button>
      </div>

      {loading ? (
        <div className="text-slate-500 font-mono">Cargando…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {users.map((u) => (
            <div key={u.id} className={`card-base p-4 ${!u.active ? "opacity-60" : ""}`} data-testid={`user-card-${u.email}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-heading font-bold truncate">{u.name}</div>
                  <div className="text-xs text-slate-500 font-mono truncate">{u.email}</div>
                  <div className="text-xs text-blue-600 font-mono uppercase tracking-widest mt-1">{ROLE_LABEL[u.role]}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => toggleActive(u)} className="btn-ghost" title={u.active ? "Desactivar" : "Activar"} data-testid={`user-toggle-${u.email}`}>
                    {u.active ? <ShieldCheck size={16} className="text-green-600" /> : <ShieldOff size={16} className="text-red-500" />}
                  </button>
                  <button onClick={() => setModal({ ...u, password: "" })} className="btn-ghost" data-testid={`user-edit-${u.email}`}>Editar</button>
                  <button onClick={() => del(u)} className="btn-ghost text-red-500" data-testid={`user-delete-${u.email}`}><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <Modal onClose={() => setModal(null)} title={modal.id ? "Editar usuario" : "Nuevo usuario"}>
          <div className="space-y-3">
            {!modal.id && (
              <input type="email" value={modal.email} onChange={(e) => setModal({ ...modal, email: e.target.value })} placeholder="Email" className="input-base" data-testid="user-modal-email" required />
            )}
            <input value={modal.name} onChange={(e) => setModal({ ...modal, name: e.target.value })} placeholder="Nombre" className="input-base" data-testid="user-modal-name" />
            <select value={modal.role} onChange={(e) => setModal({ ...modal, role: e.target.value })} className="input-base" data-testid="user-modal-role">
              {(roles.length > 0 ? roles : [
                { key: "admin", display_name: "Administrador" },
                { key: "supervisor", display_name: "Supervisora" },
                { key: "technician", display_name: "Técnico" },
                { key: "reception", display_name: "Recepción / Vendedor" },
              ]).map((r) => (
                <option key={r.key} value={r.key}>{r.display_name}</option>
              ))}
            </select>
            <select value={modal.branch_id || ""} onChange={(e) => setModal({ ...modal, branch_id: e.target.value })} className="input-base" data-testid="user-modal-branch">
              <option value="">Sucursal (opcional)</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            {modal.role === "technician" && (
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-1">Comisión sobre ganancia (%)</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={modal.commission_rate ?? ""}
                  onChange={(e) => setModal({ ...modal, commission_rate: e.target.value })}
                  placeholder="Ej: 20"
                  className="input-base"
                  data-testid="user-modal-commission"
                />
                <span className="text-[10px] text-slate-500 mt-1 block">Dejar vacío si el técnico todavía no cobra comisión.</span>
              </label>
            )}
            <input type="password" value={modal.password || ""} onChange={(e) => setModal({ ...modal, password: e.target.value })} placeholder={modal.id ? "Nueva contraseña (dejar vacío = mantener)" : "Contraseña"} className="input-base" data-testid="user-modal-password" minLength={modal.id ? 0 : 6} />
            <button onClick={save} className="btn-primary w-full" data-testid="user-modal-save">
              <Save size={16} /> Guardar
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
